import "react-toastify/dist/ReactToastify.css";
import "react-loading-skeleton/dist/skeleton.css";

import { lazy, useEffect } from "react";
import { useIdleTimer } from "react-idle-timer";
import { ToastContainer } from "react-toastify";
import { BrowserRouter, Routes, Route } from "react-router-dom";

import {
  GENERAL_DEBOUNCE_TIME,
  CLAUSE_MANAGEMENT_VISIBLE_MODULES,
  MANAGEMENT_MODULE_DEPENDENCIES,
  MODULE_ACCESS,
  PROTECTED,
  ROUTES,
  TIMEOUT_MILLISECONDS,
} from "./common/constants";

import { logout } from "./common/common";
import { RoutesConfigType } from "./common/types";
import { UserRolesKey } from "./modules/Admin/constants";
import { SuspenseLoader } from "./components/Loaders/Loaders";
import { setupReactGA } from "./analytics/ReactGA.analytics";
import { useVersionCheck } from "./hooks/useVersionCheck";
import { useGlobalJobPoller } from "./hooks/useGlobalJobPoller";
import { ChunkErrorBoundary } from "./components/ErrorBoundary/ChunkErrorBoundary";

const ManageContract = lazy(
  () =>
    import("./modules/ManageContract/Pages/ManageContractPage/ManageContract")
);

const PageNotFound = lazy(
  () => import("./components/PageNotFound/PageNotFound")
);

const LoginPage = lazy(() => import("./containers/LoginPage/LoginPage"));

const Dashboard = lazy(
  () => import("./modules/vscDashboard/Pages/landingPage/Dashboard")
);
const EmbeddedDocument = lazy(
  () => import("./components/EmbeddedDocument/EmbeddedDocument")
);

const DashboardTabs = lazy(
  () => import("./modules/vscDashboard/Pages/dashboardTabs/DashboardTabs")
);

const UserManagement = lazy(
  () => import("./modules/Admin/Pages/UserManagement/UserManagement")
);

const VerticalManagement = lazy(
  () => import("./components/VerticalManagement/VerticalManagement")
);

const VerticalDetails = lazy(
  () => import("./components/VerticalManagement/VerticalDetails/VerticalDetails")
);

const ClauseManagement = lazy(
  () => import("./modules/Admin/Pages/PromptManagement/PromptManagement")
);

const PAConfigManager = lazy(
  () => import("./components/PAConfigManager/PAConfigManager")
);

const ContractManagement = lazy(
  () => import("./modules/Admin/Pages/ContractManagement/ContractManagement")
);

const ModuleManagement = lazy(
  () => import("./modules/Admin/Pages/ModuleManagement/ModuleManagement")
);

const LandingPage = lazy(() => import("./containers/LandingPage/LandingPage"));

const AuthContainer = lazy(
  () => import("./containers/AuthContainer/AuthContainer")
);

const AdminContainer = lazy(
  () => import("./containers/AdminContainer/AdminContainer")
);

const KCCFiles = lazy(() => import("./components/KCC/FilesScreen/KCCFiles"));

const JobDetails = lazy(
  () => import("./components/VerticalManagement/JobDetails/JobDetails")
);

const ContractDraftDashboard = lazy(
  () => import("./modules/ContractDraft/Pages/ContractDraftDashboard/ContractDraftDashboard")
);

const ContractDraftingDetail = lazy(
  () => import("./modules/ContractDraft/Pages/ContractDraftingDetail/Pages/ContractDraftingDetail")
);

const ContractDraft = lazy(
  () =>
    import("./modules/ContractDraft/Pages/ContractDraftPage/ContractDraft")
);

const PdfViewer = lazy(
  () => import("./pages/PdfViewer")
);

const TaskOrderExtractionDashboard = lazy(
  () => import("./modules/TaskOrderExtraction/Pages/TaskOrderExtractionDashboard/TaskOrderExtractionDashboard")
);

const About = lazy(() => import("./pages/About"));

const Reports = lazy(() => import("./pages/Reports"));

const RoutesConfig: RoutesConfigType = [
  {
    path: ROUTES.error,
    element: <PageNotFound />,
    nonAdminProps: {
      showSidebar: false,
    },
  },
  {
    path: ROUTES.login,
    element: <LoginPage />,
    nonAdminProps: {
      showSidebar: false,
    },
  },
  {
    path: ROUTES.dashboard,
    element: <Dashboard />,
    type: PROTECTED,
  },
  {
    path: ROUTES.doc,
    element: <EmbeddedDocument />,
    type: PROTECTED,
  },
  {
    path: ROUTES.clauseCheckDoc,
    element: <EmbeddedDocument />,
    type: PROTECTED,
  },
  {
    path: ROUTES.manageContracts,
    element: <ManageContract />,
    type: PROTECTED,
    moduleName: MODULE_ACCESS.CONTRACT_REVIEW,
    allowedUsers: [UserRolesKey.REDLINING_USER, UserRolesKey.USER, UserRolesKey.ADMIN],
  },
  {
    path: ROUTES.dashboardTabs,
    element: <DashboardTabs />,
    type: PROTECTED,
  },
  {
    path: ROUTES.keyContractClausesDashboardAdvancedDetails,
    element: <KCCFiles />,
    type: PROTECTED,
    moduleName: MODULE_ACCESS.CLAUSE_CHECK,
  },
  {
    path: ROUTES.adminDashboard,
    element: <UserManagement />,
    type: PROTECTED,
    moduleName: MODULE_ACCESS.USER_MANAGEMENT,
    allowedUsers: [UserRolesKey.ADMIN],
  },
  {
    path: ROUTES.verticalManagement,
    element: <VerticalManagement />,
    type: PROTECTED,
    moduleName: MANAGEMENT_MODULE_DEPENDENCIES[MODULE_ACCESS.VERTICAL_MANAGEMENT],
    allowedUsers: [UserRolesKey.ADMIN],
  },
  {
    path: ROUTES.verticalManagementDetails,
    element: <VerticalDetails />,
    type: PROTECTED,
    moduleName: MANAGEMENT_MODULE_DEPENDENCIES[MODULE_ACCESS.VERTICAL_MANAGEMENT],
    allowedUsers: [UserRolesKey.ADMIN],
  },
  {
    path: ROUTES.clauseManagement,
    element: (
      <ClauseManagement
        pageTitle="Clause Management"
      />
    ),
    type: PROTECTED,
    moduleName: CLAUSE_MANAGEMENT_VISIBLE_MODULES,
    allowedUsers: [UserRolesKey.ADMIN],
  },
  {
    path: ROUTES.clauseManagementContractReview,
    element: (
      <ClauseManagement
        pageTitle="Clause Management"
      />
    ),
    type: PROTECTED,
    moduleName: CLAUSE_MANAGEMENT_VISIBLE_MODULES,
    allowedUsers: [UserRolesKey.ADMIN],
  },
  {
    path: ROUTES.clauseManagementKcc,
    element: (
      <ClauseManagement
        pageTitle="Clause Management"
      />
    ),
    type: PROTECTED,
      moduleName: MANAGEMENT_MODULE_DEPENDENCIES[MODULE_ACCESS.KCC_CLAUSE_MANAGEMENT],
    allowedUsers: [UserRolesKey.ADMIN],
  },
  {
    // Power-user only — PA config governs the pipeline's doc-type hierarchy
    // and folder semantics, which is platform-level configuration, not a
    // tenant-admin concern. Same gate as moduleManagement above.
    path: ROUTES.paConfiguration,
    element: <PAConfigManager />,
    type: PROTECTED,
    allowedUsers: [UserRolesKey.POWER_USER],
  },
  {
    path: ROUTES.contractManagement,
    element: <ContractManagement />,
    type: PROTECTED,
    moduleName: MANAGEMENT_MODULE_DEPENDENCIES[MODULE_ACCESS.CONTRACT_MANAGEMENT],
    allowedUsers: [UserRolesKey.ADMIN],
  },
  {
    path: ROUTES.moduleManagement,
    element: <ModuleManagement />,
    type: PROTECTED,
    moduleName: MODULE_ACCESS.MODULE_MANAGEMENT,
    allowedUsers: [UserRolesKey.POWER_USER],
  },
  {
    path: ROUTES.jobDetails,
    element: <JobDetails />,
    type: PROTECTED,
    moduleName: MODULE_ACCESS.VERTICAL_MANAGEMENT,
    allowedUsers: [UserRolesKey.ADMIN],
  },
  {
    path: ROUTES.contractDraftDashboard,
    element: <ContractDraftDashboard />,
    type: PROTECTED,
    moduleName: MODULE_ACCESS.CONTRACT_DRAFTING,
    allowedUsers: [UserRolesKey.ADMIN, UserRolesKey.REDLINING_USER, UserRolesKey.USER],
  },
  {
    path: ROUTES.contractDraftDetail,
    element: <ContractDraftingDetail />,
    type: PROTECTED,
    moduleName: MODULE_ACCESS.CONTRACT_DRAFTING,
    allowedUsers: [UserRolesKey.ADMIN, UserRolesKey.REDLINING_USER, UserRolesKey.USER],
  },
  {
    path: ROUTES.contractDraft,
    element: <ContractDraft />,
    type: PROTECTED,
    moduleName: MODULE_ACCESS.CONTRACT_DRAFTING,
    allowedUsers: [UserRolesKey.ADMIN, UserRolesKey.REDLINING_USER, UserRolesKey.USER],
  },
  {
    path: ROUTES.pdfViewer,
    element: <PdfViewer />,
    type: PROTECTED,
  },
  {
    path: ROUTES.taskOrderExtractionDashboard,
    element: <TaskOrderExtractionDashboard />,
    type: PROTECTED,
    moduleName: MODULE_ACCESS.TASK_ORDER_EXTRACTION,
    allowedUsers: [UserRolesKey.ADMIN, UserRolesKey.REDLINING_USER, UserRolesKey.USER],
  },
  {
    path: ROUTES.about,
    element: <About />,
    type: PROTECTED,
  },
  {
    path: ROUTES.reports,
    element: <Reports />,
    type: PROTECTED,
    moduleName: MODULE_ACCESS.REPORTS,
  },
];

const GlobalJobPoller = () => {
  useGlobalJobPoller();
  return null;
};

function App() {
  const isLoginPage = window.location.pathname === ROUTES.login;
  const handleOnUserIdle = () => {
    logout('IDLE_TIMEOUT');
  };

  // Detects a tab that's been left open across a deploy and force-reloads it
  // so it never keeps running stale (potentially pre-fix) JavaScript.
  useVersionCheck();

  useIdleTimer({
    timeout: TIMEOUT_MILLISECONDS,
    onIdle: handleOnUserIdle,
    debounce: GENERAL_DEBOUNCE_TIME,
    disabled: isLoginPage,
    crossTab: true,
  });

  useEffect(() => {
    setupReactGA();
  }, []);

  return (
    <BrowserRouter>
      <GlobalJobPoller />
      <ChunkErrorBoundary>
        <Routes>{getRoutes(RoutesConfig)}</Routes>
      </ChunkErrorBoundary>
      <ToastContainer
        position="top-center"
        closeOnClick={true}
        pauseOnFocusLoss={false}
        newestOnTop={true}
        pauseOnHover={false}
      />
    </BrowserRouter>
  );
}

const getRoutes = (RoutesConfig: RoutesConfigType) => {
  return RoutesConfig.map((route, routeIndex) => {
    let { element, path, adminProps, allowedUsers, moduleName, nonAdminProps, type } =
      route;
    const isProtectedRoute = type === PROTECTED;
    const isAdminRoute =
      allowedUsers?.length === 1 &&
      (allowedUsers?.includes(UserRolesKey.ADMIN) ||
        allowedUsers?.includes(UserRolesKey.POWER_USER));

    if (isAdminRoute) {
      element = <AdminContainer {...adminProps}>{element}</AdminContainer>;
    } else {
      element = <LandingPage {...nonAdminProps}>{element}</LandingPage>;
    }

    if (isProtectedRoute) {
      element = (
        <AuthContainer allowedUsers={allowedUsers} moduleName={moduleName}>{element}</AuthContainer>
      );
    }

    element = <SuspenseLoader>{element}</SuspenseLoader>;

    return <Route key={routeIndex} path={path} element={element} />;
  });
};

export default App;
